/**
 * Variant Rule toggles (issue #12). Switching a rule on or off changes which Choices the
 * progression opens (Free Archetype, Ancestry Paragon, Gradual Attribute Boosts, Automatic Bonus
 * Progression's skill potencies and apex, the Dual Class second class and its extra slots) and
 * what the engine computes (ABP's potencies, Proficiency Without Level). `setVariantRules` applies
 * a toggle off to the side and reports what it opened, what it removed (a Choice the rule owned,
 * with its Selection or answer) and what it reopened (a Selection made illegal by the change), so
 * the caller can show the summary before committing.
 */
import type { Character, Choice, Level, LevelNumber, VariantRules } from "../character/model";
import { choiceKey, isGranted, isLevelNumber, parseChoiceKey } from "../character/model";
import { emptyLevel } from "../character/create";
import { clearSelection } from "../character/grants";
import { BOOST_LEVELS } from "./slots";
import { reopenIllegal, type Reopened, type Settle } from "./changes";
import type { GuideContext } from "./context";
import { guideLevels } from "./steps";

export interface VariantChoice {
  key: string;
  level: LevelNumber;
  label: string;
}

export interface VariantOutcome {
  /** The settled context under the new rules, with the reopened Choices cleared. */
  ctx: GuideContext;
  rules: VariantRules;
  /** Choices the toggle opened. */
  opened: VariantChoice[];
  /** Filled Choices the toggle removed, with what filled them. */
  removed: (VariantChoice & { selection: string | null })[];
  /** Boost answers Gradual Attribute Boosts moved between a set's Levels (the numbers do not change). */
  moved: VariantMove[];
  /** Selections and Answers the toggle made illegal, cleared. */
  reopened: Reopened[];
}

export interface VariantMove {
  from: string;
  to: string;
  fromLevel: LevelNumber;
  toLevel: LevelNumber;
  label: string;
}

export const VARIANT_RULE_LABELS: Record<keyof VariantRules, string> = {
  freeArchetype: "Free Archetype",
  automaticBonusProgression: "Automatic Bonus Progression",
  gradualAttributeBoosts: "Gradual Attribute Boosts",
  ancestryParagon: "Ancestry Paragon",
  dualClass: "Dual Class",
  proficiencyWithoutLevel: "Proficiency Without Level",
};

export const VARIANT_RULE_KEYS: readonly (keyof VariantRules)[] = ["freeArchetype", "automaticBonusProgression", "gradualAttributeBoosts", "ancestryParagon", "dualClass", "proficiencyWithoutLevel"];

/** Every user-facing Choice key on a Character up to its Level, with the label the guide gives it. */
function labelledKeys(ctx: GuideContext): Map<string, VariantChoice> {
  const out = new Map<string, VariantChoice>();
  for (const level of guideLevels(ctx)) {
    for (const item of level.items) {
      if (item.kind === "answer" || item.kind === "flag") continue;
      if (out.has(item.choiceKey)) continue;
      out.set(item.choiceKey, { key: item.choiceKey, level: item.level, label: item.label });
    }
  }
  return out;
}

/**
 * Set the Character's Variant Rules and settle. Filled Choices the new rules no longer open (the
 * ones `openProgression` reports as extra that were not extra before) are removed with their
 * Selections, granted Entries included; then whatever the change made illegal is reopened.
 */
export async function setVariantRules(ctx: GuideContext, rules: VariantRules, settle: Settle): Promise<VariantOutcome> {
  const before = ctx.character;
  if (VARIANT_RULE_KEYS.every((k) => before.variantRules[k] === rules[k])) return { ctx, rules, opened: [], removed: [], moved: [], reopened: [] };
  const labelsBefore = labelledKeys(ctx);
  // Gradual Attribute Boosts spreads a set's four boosts over four Levels: the answers move with the rule rather than being lost.
  const spread = before.variantRules.gradualAttributeBoosts !== rules.gradualAttributeBoosts ? redistributeBoosts(before, rules.gradualAttributeBoosts) : { character: before, moved: [] };
  let current = await settle({ ...spread.character, variantRules: { ...rules } });
  // Remove what the toggle orphaned.
  const removed: VariantOutcome["removed"] = [];
  let character = current.character;
  for (const key of current.extra) {
    if (ctx.extra.has(key)) continue;
    const parsed = parseChoiceKey(key);
    if (!parsed || isGranted(parsed.source)) continue;
    const level = character.levels.find((l) => l.number === parsed.level);
    const choice = level?.choices.find((c) => choiceKey(parsed.level, c.source) === key);
    if (!choice) continue;
    const selectionName = choice.selection ? (current.entries.get(choice.selection.uuid)?.name ?? current.catalogue.byUuid(choice.selection.uuid)?.n ?? choice.selection.uuid) : answerSummary(choice.answers);
    if (choice.selection) {
      const cleared = clearSelection(character, key, current.sheet);
      if (cleared.blocked) continue;
      character = cleared.character;
    }
    character = { ...character, levels: character.levels.map((l) => (l.number === parsed.level ? { ...l, choices: l.choices.filter((c) => choiceKey(l.number, c.source) !== key) } : l)) };
    removed.push({ ...(labelsBefore.get(key) ?? { key, level: parsed.level, label: key }), selection: selectionName });
  }
  if (character !== current.character) current = await settle(character);
  const settled = await reopenIllegal(current, settle);
  const labelsAfter = labelledKeys(settled.ctx);
  const movedTo = new Set(spread.moved.map((m) => m.to));
  const opened = [...labelsAfter.values()].filter((c) => !labelsBefore.has(c.key) && !movedTo.has(c.key));
  return { ctx: settled.ctx, rules, opened, removed, moved: spread.moved, reopened: settled.reopened };
}

const BOOST_SOURCE = (ordinal: number): Choice["source"] => ({ kind: "progression", slot: "attribute-boosts", ordinal });

/**
 * Move a set's answered free boosts between its Levels: to one per Level over the four Levels
 * ending at the set's Level when Gradual Attribute Boosts comes on, back to the set's Level
 * (planned ahead, if the Character has not reached it) when it goes off. The order within the
 * set is kept, so partial boosts land as before.
 */
export function redistributeBoosts(character: Character, gradual: boolean): { character: Character; moved: VariantMove[] } {
  const moved: VariantMove[] = [];
  const levels: Level[] = character.levels.map((l) => ({ ...l, choices: [...l.choices] }));
  const at = (n: LevelNumber): Level => {
    while (levels.length < n) levels.push(emptyLevel((levels.length + 1) as LevelNumber));
    return levels[n - 1]!;
  };
  const isBoost = (c: Choice): c is Choice & { source: { kind: "progression"; slot: "attribute-boosts"; ordinal: number } } => c.source.kind === "progression" && c.source.slot === "attribute-boosts";
  for (const bucket of BOOST_LEVELS) {
    if (bucket === 1) continue;
    const range = [bucket - 3, bucket - 2, bucket - 1, bucket].filter(isLevelNumber);
    const carried: { key: string; level: LevelNumber; attribute: string }[] = [];
    for (const n of range) {
      const level = levels[n - 1];
      if (!level) continue;
      for (const c of [...level.choices].filter(isBoost).sort((a, b) => a.source.ordinal - b.source.ordinal)) {
        const value = c.answers?.boost;
        if (typeof value === "string" && value) carried.push({ key: choiceKey(n, c.source), level: n, attribute: value });
      }
      level.choices = level.choices.filter((c) => !isBoost(c));
    }
    const targets = gradual ? range.map((n) => ({ level: n, ordinal: 0 })) : [0, 1, 2, 3].map((ordinal) => ({ level: bucket, ordinal }));
    carried.forEach((answer, i) => {
      const target = targets[i];
      if (!target) return;
      const source = BOOST_SOURCE(target.ordinal);
      at(target.level).choices.push({ source, answers: { boost: answer.attribute } });
      const to = choiceKey(target.level, source);
      if (to !== answer.key) moved.push({ from: answer.key, to, fromLevel: answer.level, toLevel: target.level, label: `${ATTRIBUTE_NAMES[answer.attribute] ?? answer.attribute} boost` });
    });
  }
  return { character: { ...character, levels }, moved };
}

const ATTRIBUTE_NAMES: Record<string, string> = { str: "Strength", dex: "Dexterity", con: "Constitution", int: "Intelligence", wis: "Wisdom", cha: "Charisma" };

function answerSummary(answers: Record<string, unknown> | undefined): string | null {
  const values = Object.values(answers ?? {}).filter((v): v is string => typeof v === "string" && v !== "");
  return values.length > 0 ? values.join(", ") : null;
}
